import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  Sparkles, ListChecks, UtensilsCrossed, Vote, ShoppingBag, Monitor, Megaphone,
  PartyPopper, Wrench, BookOpen, CalendarClock, QrCode, ArrowRight, Check, Pin,
  Repeat, Star, Users, Home, ArrowUp, Quote, Mail, Phone, Languages, Gift,
} from "lucide-react";
import { API } from "@/lib/api";
import { LANGS, RTL_LANGS } from "@/lib/onboarding";

const WALL = "https://static.prod-images.emergentagent.com/jobs/36eb6e84-d5f5-455c-b149-146151d45689/images/ba6e48a9b58b03b472f092eaf25fc7ae48dbaa6aaa044f5996168a8b5fc4f543.png";
const STUDENTS = "https://images.pexels.com/photos/7683897/pexels-photo-7683897.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";
const PIZZA = "https://images.pexels.com/photos/6223186/pexels-photo-6223186.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=200&w=200";
const NOODLES = "https://images.unsplash.com/photo-1664337873053-840ea51d271d?crop=entropy&cs=srgb&fm=jpg&q=70&w=200";

const euro = (c) => c == null ? null : `€ ${(c / 100).toFixed(2).replace(".", ",")}`;

// ---- Full landing copy per language ----
const TX = {
  nl: {
    nav: { features: "Features", kiosk: "Kiosk", reviews: "Reviews", pricing: "Prijzen", login: "Inloggen", tryFree: "Gratis proberen" },
    hero: {
      badge: "Voor gezinnen, studentenhuizen, verenigingen & instellingen",
      titleA: "Eindelijk overzicht in de ", titleB: "regel-chaos", titleC: ".",
      sub: "Taken, maaltijden, boodschappen en iedereen erbij — zonder eindeloos te appen. Slackr laat je huishouden, gezin of vereniging soepel draaien.",
      cta: "Gratis proberen", how: "Hoe het werkt",
      trial: "{d} dagen gratis · geen creditcard nodig",
      toastDone: "Taak voltooid!", birthday: "Daan is jarig 🎂",
    },
    kiosk: {
      tag: "Kiosk-modus", title: "Het digitale hart van je huis, aan de muur.",
      sub: "Hang een tablet (11\") op als een schilderij en iedereen is altijd bij. Mededelingen bovenaan, het rooster van vandaag, de eetpoll en snelle PIN-logins — zonder in te loggen.",
      points: ["Mededelingen altijd bovenaan", "Snel een taak overnemen met je PIN", "Live poll: wat eten we vandaag?", "Gedeelde boodschappenlijst — live afvinken (link/QR)", "Agenda-knop: zet activiteiten in je eigen agenda"],
    },
    multilang: {
      tag: "Meertalig", title: "Iedereen in z'n eigen taal",
      sub: "Slackr spreekt Nederlands, English, Türkçe én العربية. Elke huisgenoot kiest z'n eigen taal — de app, meldingen én berichten van anderen worden automatisch vertaald.",
      points: ["App & rondleiding in 4 talen", "Berichten automatisch vertaald naar jóuw taal", "Kioskbord in de taal die de beheerder kiest", "Volledige ondersteuning voor rechts-naar-links (Arabisch)"],
      example: "Voorbeeld: Youssef plaatst een mededeling in het Arabisch, Sanne leest 'm in het Nederlands en Mert in het Turks — niemand hoeft iets te vertalen.",
    },
    features: {
      header: "Alles wat een huishouden nodig heeft, in één app.", example: "Voorbeeld",
      kbPreview: { tag: "Hoedan", title: "Koffieapparaat reinigen", steps: ["Zet uit en laat afkoelen", "Spoel het filter met warm water", "Ontkalk 1× per maand"] },
      items: [
        { badge: "Taken & Rooster", title: "Eerlijk verdeeld, soepel gepland",
          text: "Sleep taken tussen dagen in het visuele rooster, stel ze uit met één tik (\"kan vandaag niet, wel vrijdag\") en laat de auto-planner ze eerlijk verdelen.",
          scenario: "Daan is ziek? Sleep zijn corvee naar morgen — iedereen ziet het meteen op de Kiosk." },
        { badge: "Eten & Boodschappen", title: "Nooit meer \u2018wat eten we vandaag?\u2019",
          text: "Recepten met portie-schaler, slimme boodschappenlijstjes en een dagelijkse fotopoll om samen te kiezen. Deel de lijst via link of QR, vink live af op de Kiosk en zet een recept met één tik door naar je lijst.",
          scenario: "Poll met foto's: \u2018Pasta Pesto\u2019 of \u2018Chinees\u2019? Het huis stemt, de winnaar staat op het bord." },
        { badge: "Hoppa Marktplaats", title: "Lenen, huren, kopen of gratis",
          text: "Een eigen marktplaats binnen je huis of instelling. Boormachine te leen, oud bureau te koop of verhuisdozen gratis weg — met AI-omschrijvingen.",
          scenario: "\u2018Wie heeft er een boormachine?\u2019 Plaats 'm op Hoppa en klaar — 8 weken online." },
        { badge: "Klusjes & Huiszaken", title: "Van kapotte kraan tot huisfeest",
          text: "Meld klusjes als tickets voor de facilitaire dienst, pin belangrijke mededelingen bovenaan en vier verjaardagen met automatische cadeau-reminders.",
          scenario: "Lekkende kraan? Maak een klusje aan met foto — de beheerder pakt 'm op." },
        { badge: "Cadeaupotten", title: "Samen sparen voor het perfecte cadeau",
          text: "Start een cadeaupot voor élke gelegenheid — verjaardag, afscheid, housewarming of iets leuks voor het huis. Zet een streefbedrag, verzamel cadeau-ideeën en plak een Tikkie-link of IBAN erbij. Kies je een ontvanger, dan blijft de pot voor hem/haar geheim.",
          scenario: "Jan gaat verhuizen? Start een pot, iedereen doet mee via Tikkie — en Jan ziet niks. \ud83e\udd2b" },
        { badge: "Huis-uitleg & Kennisbank", title: "Leg uit hoe alles in huis werkt",
          text: "Van het koffieapparaat reinigen tot de tuinsproeiers aanzetten — leg met foto's en stappen vast hoe apparaten en klusjes in en om het huis werken. Nieuwe huisgenoten lezen het zo na en Wizzy AI beantwoordt de rest.",
          scenario: "\u2018Hoe zet ik de tuinsproeiers aan?\u2019 Staat met foto's in de kennisbank — Wizzy legt de stappen uit." },
      ],
    },
    modulesHeader: "En nog veel meer",
    modules: [
      { label: "Mededelingen", d: "Pin belangrijke berichten, plan ze in of laat ze herhalen." },
      { label: "Vieringen", d: "Verjaardagen & feestjes met cadeau-reminders vooraf." },
      { label: "Taakruilingen", d: "Ruil corvee eerlijk met huisgenoten via de ruilmarkt." },
      { label: "Kennisbank + Wizzy AI", d: "\u2018Hoedan, Wattan\u2019 — vraag Wizzy hoe alles werkt." },
      { label: "Pin Dates", d: "Belangrijke gebeurtenissen met kalender & logboek." },
      { label: "Uitnodigen via QR", d: "Nieuwe huisgenoten scannen en melden zich aan." },
      { label: "Boodschappen delen", d: "Deel de lijst via link of QR — iedereen vinkt live af, ook op de Kiosk." },
      { label: "Agenda in je telefoon", d: "Abonneer je op je taken & activiteiten — ze staan in je eigen agenda." },
    ],
    how: {
      header: "In 3 stappen aan de slag",
      steps: [
        { t: "Maak je huis aan", d: "Voeg groepen, ruimtes en voorkeuren toe — in een paar minuten." },
        { t: "Nodig iedereen uit", d: "Deel de QR-code of invite-link. Huisgenoten melden zich aan en installeren de app." },
        { t: "Slackr doet de rest", d: "Wizzy AI en de auto-planner houden het huis draaiende en overzichtelijk." },
      ],
    },
    reviews: {
      avg: "4,9 / 5 gemiddeld", header: "Wat gebruikers zeggen", sub: "Van studentenhuizen tot verenigingen en gezinnen.",
      items: [
        { q: "Sinds Slackr is het eindeloze appen over wie de afwas doet echt voorbij. Iedereen ziet gewoon wat er moet gebeuren.", n: "Sanne", r: "Studentenhuis, Utrecht" },
        { q: "De kiosk aan de muur is goud waard. De kids checken 's ochtends zelf hun taken — geen gezeur meer.", n: "Mark & Elske", r: "Gezin, 3 kinderen" },
        { q: "Wij gebruiken het voor onze vereniging. Klusjes, roosters en mededelingen op één plek. Superoverzichtelijk.", n: "Youssef", r: "Sportvereniging" },
        { q: "Eindelijk een app die niet ingewikkeld is. Binnen 10 minuten stond ons hele huishouden erop.", n: "Priya", r: "Woongroep, Rotterdam" },
        { q: "De 'Wat eten we vandaag?'-polls met foto's zijn een hit. Koken plannen was nog nooit zo leuk.", n: "Tom", r: "Studentenhuis, Groningen" },
        { q: "Als zorginstelling houden we bewonerstaken en activiteiten netjes bij. Het team is er blij mee.", n: "Linda", r: "Zorginstelling" },
      ],
    },
    pricing: {
      header: "Eenvoudige pakketten voor elk huis",
      sub: "Start altijd met {d} dagen gratis proberen. Geen creditcard nodig.",
      popular: "Populair", perMonth: "per maand", cta: "Gratis proberen",
      months: "mnd",
      plans: [
        { name: "3 Maanden", tag: "Kort & krachtig", feats: ["Alle modules", "Kiosk-modus", "Wizzy AI", "Geen setupkosten"] },
        { name: "6 Maanden", tag: "Populair", feats: ["Alle modules", "Kiosk-modus", "Wizzy AI", "Voorrang support"] },
        { name: "12 Maanden", tag: "Meeste waarde", feats: ["Alle modules", "Kiosk-modus", "Wizzy AI", "Extra admin-accounts"] },
      ],
    },
    finalCta: {
      header: "Klaar voor een opgeruimd huis?",
      sub: "Sluit je aan bij huizen die de chaos achter zich lieten. Meld je aan, installeer de app en begin vandaag nog.",
      cta: "Start je gratis proefperiode",
    },
    footer: { tagline: "Taken zonder eindeloos te appen — voor gezinnen, studentenhuizen, verenigingen & instellingen.", contact: "Contact", getStarted: "Aan de slag", login: "Inloggen", tryFree: "Gratis proberen" },
    contact: { tag: "Contact", title: "Vragen, ideeën of hulp nodig?", sub: "Laat een bericht achter — we reageren zo snel mogelijk.", types: { vraag: "Vraag", suggestie: "Suggestie", support: "Support" }, name: "Naam", email: "E-mailadres", message: "Je bericht", placeholder: "Waar kunnen we je mee helpen?", send: "Versturen", sending: "Versturen…", ok: "Bedankt! We hebben je bericht ontvangen. Je krijgt een bevestiging per e-mail.", err: "Er ging iets mis. Probeer het opnieuw." },
    demo: {
      greeting: "Goedemorgen, Sanne 👋", pinned: "Vastgezet", wifiTitle: "WiFi-wachtwoord gewijzigd", wifiBody: "Hangt op het prikbord in de keuken.",
      openTasks: "Open taken", forYou: "Voor jou", task1: "Afwas doen", task2: "Stofzuigen woonkamer",
      roosterTitle: "Rooster", roosterSub: "Sleep taken naar een andere dag", postponed: "Stofzuigen uitgesteld → wo",
      etenTitle: "Wat eten we vandaag?", etenSub: "Stem mee met je huisgenoten", meal1: "Pizza-avond", meal2: "Chinees halen",
      hoppaTitle: "Hoppa marktplaats", item1: "Accuboormachine", lend: "LENEN", item1sub: "Incl. bitset · Thomas · Utrecht", item2: "Stadsfiets — huur", perWeek: "/wk",
      days: ["Ma", "Di", "Wo", "Do", "Vr", "Za", "Zo"],
      kioskHeader: "Kiosk · woensdag", kTask1: "Afwas doen — Sanne", kTask2: "Vuilnis buiten — Daan", kTask3: "Planten water — Emma",
      important: "Belangrijk", meeting: "Huisvergadering vr. 19:30", whatEat: "Wat eten we?", pasta: "Pasta 🍝",
      agenda: "Agenda", shopTitle: "Boodschappen", shop1: "Melk", shop2: "Brood",
    },
  },
  en: {
    nav: { features: "Features", kiosk: "Kiosk", reviews: "Reviews", pricing: "Pricing", login: "Log in", tryFree: "Try for free" },
    hero: {
      badge: "For families, student houses, associations & institutions",
      titleA: "Finally clarity in the ", titleB: "coordination chaos", titleC: ".",
      sub: "Tasks, meals, groceries and everyone involved — without endless texting. Slackr keeps your household, family or association running smoothly.",
      cta: "Try for free", how: "How it works",
      trial: "{d} days free · no credit card needed",
      toastDone: "Task completed!", birthday: "It's Daan's birthday 🎂",
    },
    kiosk: {
      tag: "Kiosk mode", title: "The digital heart of your home, on the wall.",
      sub: "Mount a tablet (11\") like a painting and everyone is always up to date. Announcements on top, today's schedule, the meal poll and quick PIN logins — without logging in.",
      points: ["Announcements always on top", "Quickly take over a task with your PIN", "Live poll: what shall we eat today?", "Shared grocery list — tick off live (link/QR)", "Calendar button: add events to your own calendar"],
    },
    multilang: {
      tag: "Multilingual", title: "Everyone in their own language",
      sub: "Slackr speaks Nederlands, English, Türkçe and العربية. Each housemate picks their own language — the app, notifications and messages from others are translated automatically.",
      points: ["App & tour in 4 languages", "Messages auto-translated into your language", "Kiosk board in the language the admin picks", "Full right-to-left support (Arabic)"],
      example: "Example: Youssef posts an announcement in Arabic, Sanne reads it in Dutch and Mert in Turkish — nobody has to translate anything.",
    },
    features: {
      header: "Everything a household needs, in one app.", example: "Example",
      kbPreview: { tag: "How-to", title: "Cleaning the coffee machine", steps: ["Switch off and let it cool", "Rinse the filter with warm water", "Descale once a month"] },
      items: [
        { badge: "Tasks & Schedule", title: "Fairly shared, smoothly planned",
          text: "Drag tasks between days in the visual schedule, postpone them with one tap (\"can't today, but Friday works\") and let the auto-planner share them fairly.",
          scenario: "Daan is sick? Drag his chore to tomorrow — everyone sees it instantly on the Kiosk." },
        { badge: "Food & Groceries", title: "No more \u2018what shall we eat today?\u2019",
          text: "Recipes with a portion scaler, smart grocery lists and a daily photo poll to choose together. Share the list via link or QR, tick items off live on the Kiosk, and send a recipe to your list with one tap.",
          scenario: "Photo poll: \u2018Pasta Pesto\u2019 or \u2018Chinese takeaway\u2019? The house votes, the winner shows on the board." },
        { badge: "Hoppa Marketplace", title: "Borrow, rent, buy or free",
          text: "Your own marketplace inside your house or institution. A drill to borrow, an old desk for sale or moving boxes for free — with AI descriptions.",
          scenario: "\u2018Who has a drill?\u2019 Post it on Hoppa and done — online for 8 weeks." },
        { badge: "Chores & Home matters", title: "From broken tap to house party",
          text: "Report chores as tickets for the facility team, pin important announcements on top and celebrate birthdays with automatic gift reminders.",
          scenario: "Leaking tap? Create a chore with a photo — the admin picks it up." },
        { badge: "Gift pots", title: "Save together for the perfect gift",
          text: "Start a gift pot for any occasion — birthday, farewell, housewarming or something nice for the house. Set a target, gather gift ideas and add a Tikkie link or IBAN. Pick a recipient and the pot stays a secret for them.",
          scenario: "Jan is moving out? Start a pot, everyone chips in via a payment link — and Jan sees nothing. \ud83e\udd2b" },
        { badge: "House know-how & Knowledge base", title: "Explain how everything in the house works",
          text: "From cleaning the coffee machine to turning on the garden sprinklers — capture with photos and steps how appliances and chores in and around the house work. New housemates just read it up and Wizzy AI answers the rest.",
          scenario: "\u2018How do I turn on the sprinklers?\u2019 It's in the knowledge base with photos — Wizzy explains the steps." },
      ],
    },
    modulesHeader: "And much more",
    modules: [
      { label: "Announcements", d: "Pin important messages, schedule them or have them repeat." },
      { label: "Celebrations", d: "Birthdays & parties with gift reminders in advance." },
      { label: "Task swaps", d: "Swap chores fairly with housemates via the swap market." },
      { label: "Knowledge base + Wizzy AI", d: "\u2018How-to, What-is\u2019 — ask Wizzy how everything works." },
      { label: "Pin Dates", d: "Important events with a calendar & log." },
      { label: "Invite via QR", d: "New housemates scan and sign up." },
      { label: "Share groceries", d: "Share the list via link or QR — everyone ticks off live, also on the Kiosk." },
      { label: "Calendar sync", d: "Subscribe to your tasks & activities — they appear in your own calendar." },
    ],
    how: {
      header: "Get started in 3 steps",
      steps: [
        { t: "Create your home", d: "Add groups, rooms and preferences — in just a few minutes." },
        { t: "Invite everyone", d: "Share the QR code or invite link. Housemates sign up and install the app." },
        { t: "Slackr does the rest", d: "Wizzy AI and the auto-planner keep the house running and organized." },
      ],
    },
    reviews: {
      avg: "4.9 / 5 average", header: "What users say", sub: "From student houses to associations and families.",
      items: [
        { q: "Since Slackr the endless texting about who does the dishes is truly over. Everyone just sees what needs doing.", n: "Sanne", r: "Student house, Utrecht" },
        { q: "The kiosk on the wall is worth its weight in gold. The kids check their tasks themselves in the morning — no more nagging.", n: "Mark & Elske", r: "Family, 3 kids" },
        { q: "We use it for our association. Chores, schedules and announcements in one place. Super clear.", n: "Youssef", r: "Sports club" },
        { q: "Finally an app that isn't complicated. Within 10 minutes our whole household was on it.", n: "Priya", r: "Co-living, Rotterdam" },
        { q: "The 'what shall we eat today?' polls with photos are a hit. Planning meals was never this fun.", n: "Tom", r: "Student house, Groningen" },
        { q: "As a care institution we keep resident tasks and activities neatly tracked. The team loves it.", n: "Linda", r: "Care institution" },
      ],
    },
    pricing: {
      header: "Simple plans for every home",
      sub: "Always start with {d} days free. No credit card needed.",
      popular: "Popular", perMonth: "per month", cta: "Try for free",
      months: "mo",
      plans: [
        { name: "3 Months", tag: "Short & sweet", feats: ["All modules", "Kiosk mode", "Wizzy AI", "No setup fees"] },
        { name: "6 Months", tag: "Popular", feats: ["All modules", "Kiosk mode", "Wizzy AI", "Priority support"] },
        { name: "12 Months", tag: "Best value", feats: ["All modules", "Kiosk mode", "Wizzy AI", "Extra admin accounts"] },
      ],
    },
    finalCta: {
      header: "Ready for a tidy home?",
      sub: "Join homes that left the chaos behind. Sign up, install the app and start today.",
      cta: "Start your free trial",
    },
    footer: { tagline: "Tasks without endless texting — for families, student houses, associations & institutions.", contact: "Contact", getStarted: "Get started", login: "Log in", tryFree: "Try for free" },
    contact: { tag: "Contact", title: "Questions, ideas or need help?", sub: "Leave a message — we'll get back to you as soon as possible.", types: { vraag: "Question", suggestie: "Suggestion", support: "Support" }, name: "Name", email: "Email address", message: "Your message", placeholder: "How can we help you?", send: "Send", sending: "Sending…", ok: "Thanks! We received your message. You'll get a confirmation by email.", err: "Something went wrong. Please try again." },
    demo: {
      greeting: "Good morning, Sanne 👋", pinned: "Pinned", wifiTitle: "WiFi password changed", wifiBody: "It's on the notice board in the kitchen.",
      openTasks: "Open tasks", forYou: "For you", task1: "Do the dishes", task2: "Vacuum the living room",
      roosterTitle: "Schedule", roosterSub: "Drag tasks to another day", postponed: "Vacuuming postponed → Wed",
      etenTitle: "What shall we eat today?", etenSub: "Vote with your housemates", meal1: "Pizza night", meal2: "Chinese takeaway",
      hoppaTitle: "Hoppa marketplace", item1: "Cordless drill", lend: "BORROW", item1sub: "Incl. bit set · Thomas · Utrecht", item2: "City bike — rent", perWeek: "/wk",
      days: ["Mo", "Tu", "We", "Th", "Fr", "Sa", "Su"],
      kioskHeader: "Kiosk · Wednesday", kTask1: "Do the dishes — Sanne", kTask2: "Take out trash — Daan", kTask3: "Water plants — Emma",
      important: "Important", meeting: "House meeting Fri 7:30pm", whatEat: "What shall we eat?", pasta: "Pasta 🍝",
      agenda: "Calendar", shopTitle: "Groceries", shop1: "Milk", shop2: "Bread",
    },
  },
  tr: {
    nav: { features: "Özellikler", kiosk: "Kiosk", reviews: "Yorumlar", pricing: "Fiyatlar", login: "Giriş yap", tryFree: "Ücretsiz dene" },
    hero: {
      badge: "Aileler, öğrenci evleri, dernekler ve kurumlar için",
      titleA: "Organizasyon kaosunda ", titleB: "nihayet düzen", titleC: ".",
      sub: "Görevler, yemekler, alışveriş ve herkes bir arada — sonu gelmeyen mesajlaşma olmadan. Slackr evini, aileni veya derneğini sorunsuz çalıştırır.",
      cta: "Ücretsiz dene", how: "Nasıl çalışır",
      trial: "{d} gün ücretsiz · kredi kartı gerekmez",
      toastDone: "Görev tamamlandı!", birthday: "Daan'ın doğum günü 🎂",
    },
    kiosk: {
      tag: "Kiosk modu", title: "Evinin dijital kalbi, duvarda.",
      sub: "Bir tableti (11\") tablo gibi as, herkes her zaman güncel kalsın. Duyurular en üstte, bugünün programı, yemek anketi ve hızlı PIN girişleri — giriş yapmadan.",
      points: ["Duyurular her zaman en üstte", "PIN ile bir görevi hızlıca devral", "Canlı anket: bugün ne yiyoruz?", "Paylaşılan alışveriş listesi — canlı işaretle (bağlantı/QR)", "Takvim düğmesi: etkinlikleri kendi takvimine ekle"],
    },
    multilang: {
      tag: "Çok dilli", title: "Herkes kendi dilinde",
      sub: "Slackr Nederlands, English, Türkçe ve العربية konuşur. Her ev arkadaşı kendi dilini seçer — uygulama, bildirimler ve başkalarının mesajları otomatik çevrilir.",
      points: ["Uygulama ve tur 4 dilde", "Mesajlar senin diline otomatik çevrilir", "Kiosk panosu yöneticinin seçtiği dilde", "Tam sağdan sola desteği (Arapça)"],
      example: "Örnek: Youssef Arapça bir duyuru yazar, Sanne onu Hollandaca, Mert Türkçe okur — kimsenin çeviri yapması gerekmez.",
    },
    features: {
      header: "Bir evin ihtiyaç duyduğu her şey, tek uygulamada.", example: "Örnek",
      kbPreview: { tag: "Nasıl", title: "Kahve makinesini temizleme", steps: ["Kapat ve soğumaya bırak", "Filtreyi ılık suyla durula", "Ayda bir kireç çöz"] },
      items: [
        { badge: "Görevler & Program", title: "Adil paylaşılır, kolayca planlanır",
          text: "Görsel programda görevleri günler arasında sürükle, tek dokunuşla ertele (\"bugün olmaz ama cuma olur\") ve otomatik planlayıcı adilce dağıtsın.",
          scenario: "Daan hasta mı? Görevini yarına sürükle — herkes Kiosk'ta anında görür." },
        { badge: "Yemek & Alışveriş", title: "Artık \u2018bugün ne yiyoruz?\u2019 yok",
          text: "Porsiyon ölçekleyicili tarifler, akıllı alışveriş listeleri ve birlikte seçmek için günlük fotoğraflı anket. Listeyi bağlantı veya QR ile paylaş, Kiosk'ta canlı işaretle ve bir tarifi tek dokunuşla listene ekle.",
          scenario: "Fotoğraflı anket: \u2018Pesto Makarna\u2019 mı \u2018Çin yemeği\u2019 mi? Ev oy verir, kazanan panoda." },
        { badge: "Hoppa Pazar Yeri", title: "Ödünç al, kirala, satın al veya bedava",
          text: "Evin veya kurumun içinde kendi pazar yerin. Ödünç matkap, satılık eski masa ya da bedava taşıma kutuları — yapay zeka açıklamalarıyla.",
          scenario: "\u2018Kimde matkap var?\u2019 Hoppa'ya koy ve tamam — 8 hafta yayında." },
        { badge: "İşler & Ev meseleleri", title: "Bozuk musluktan ev partisine",
          text: "İşleri tesis ekibi için talep olarak bildir, önemli duyuruları en üste sabitle ve doğum günlerini otomatik hediye hatırlatmalarıyla kutla.",
          scenario: "Akıtan musluk mu? Fotoğraflı bir iş oluştur — yönetici üstlenir." },
        { badge: "Hediye kasaları", title: "Mükemmel hediye için birlikte biriktirin",
          text: "Her durum için bir hediye kasası başlat — doğum günü, veda, ev partisi veya ev için güzel bir şey. Hedef belirle, hediye fikirleri topla ve bir Tikkie bağlantısı veya IBAN ekle. Bir alıcı seçersen kasa ondan gizli kalır.",
          scenario: "Jan taşınıyor mu? Bir kasa başlat, herkes bağlantı ile katılır — ve Jan hiçbir şey görmez. \ud83e\udd2b" },
        { badge: "Ev bilgisi & Bilgi bankası", title: "Evde her şeyin nasıl çalıştığını anlat",
          text: "Kahve makinesini temizlemekten bahçe fıskiyelerini açmaya kadar — evdeki ve çevresindeki cihazların ve işlerin nasıl çalıştığını fotoğraf ve adımlarla kaydet. Yeni ev arkadaşları okur, gerisini Wizzy AI yanıtlar.",
          scenario: "\u2018Fıskiyeleri nasıl açarım?\u2019 Fotoğraflarla bilgi bankasında — Wizzy adımları anlatır." },
      ],
    },
    modulesHeader: "Ve çok daha fazlası",
    modules: [
      { label: "Duyurular", d: "Önemli mesajları sabitle, planla veya tekrarlat." },
      { label: "Kutlamalar", d: "Doğum günleri ve partiler, önceden hediye hatırlatmalarıyla." },
      { label: "Görev takasları", d: "İşleri ev arkadaşlarınla takas pazarından adilce takas et." },
      { label: "Bilgi bankası + Wizzy AI", d: "\u2018Nasıl, Nedir\u2019 — her şeyin nasıl çalıştığını Wizzy'ye sor." },
      { label: "Pin Dates", d: "Takvim ve kayıt defteriyle önemli etkinlikler." },
      { label: "QR ile davet", d: "Yeni ev arkadaşları tarar ve kaydolur." },
      { label: "Alışverişi paylaş", d: "Listeyi bağlantı veya QR ile paylaş — herkes canlı işaretler, Kiosk'ta da." },
      { label: "Takvim senkronu", d: "Görev ve etkinliklerine abone ol — kendi takviminde görünür." },
    ],
    how: {
      header: "3 adımda başla",
      steps: [
        { t: "Evini oluştur", d: "Gruplar, odalar ve tercihler ekle — sadece birkaç dakikada." },
        { t: "Herkesi davet et", d: "QR kodunu veya davet bağlantısını paylaş. Ev arkadaşları kaydolur ve uygulamayı yükler." },
        { t: "Gerisini Slackr halleder", d: "Wizzy AI ve otomatik planlayıcı evi çalışır ve düzenli tutar." },
      ],
    },
    reviews: {
      avg: "Ortalama 4,9 / 5", header: "Kullanıcılar ne diyor", sub: "Öğrenci evlerinden derneklere ve ailelere.",
      items: [
        { q: "Slackr'dan beri bulaşığı kimin yıkayacağına dair sonu gelmeyen mesajlaşma gerçekten bitti. Herkes yapılması gerekeni görüyor.", n: "Sanne", r: "Öğrenci evi, Utrecht" },
        { q: "Duvardaki kiosk altın değerinde. Çocuklar sabahları görevlerini kendileri kontrol ediyor — artık dırdır yok.", n: "Mark & Elske", r: "Aile, 3 çocuk" },
        { q: "Derneğimiz için kullanıyoruz. İşler, programlar ve duyurular tek yerde. Çok anlaşılır.", n: "Youssef", r: "Spor kulübü" },
        { q: "Nihayet karmaşık olmayan bir uygulama. 10 dakika içinde tüm evimiz kaydoldu.", n: "Priya", r: "Ortak yaşam, Rotterdam" },
        { q: "Fotoğraflı 'bugün ne yiyoruz?' anketleri çok tuttu. Yemek planlamak hiç bu kadar eğlenceli olmamıştı.", n: "Tom", r: "Öğrenci evi, Groningen" },
        { q: "Bir bakım kurumu olarak sakin görevlerini ve etkinliklerini düzenli tutuyoruz. Ekip çok memnun.", n: "Linda", r: "Bakım kurumu" },
      ],
    },
    pricing: {
      header: "Her ev için basit paketler",
      sub: "Her zaman {d} gün ücretsiz denemeyle başla. Kredi kartı gerekmez.",
      popular: "Popüler", perMonth: "aylık", cta: "Ücretsiz dene",
      months: "ay",
      plans: [
        { name: "3 Ay", tag: "Kısa & etkili", feats: ["Tüm modüller", "Kiosk modu", "Wizzy AI", "Kurulum ücreti yok"] },
        { name: "6 Ay", tag: "Popüler", feats: ["Tüm modüller", "Kiosk modu", "Wizzy AI", "Öncelikli destek"] },
        { name: "12 Ay", tag: "En avantajlı", feats: ["Tüm modüller", "Kiosk modu", "Wizzy AI", "Ekstra yönetici hesapları"] },
      ],
    },
    finalCta: {
      header: "Düzenli bir eve hazır mısın?",
      sub: "Kaosu geride bırakan evlere katıl. Kaydol, uygulamayı yükle ve bugün başla.",
      cta: "Ücretsiz denemeni başlat",
    },
    footer: { tagline: "Sonu gelmeyen mesajlaşma olmadan görevler — aileler, öğrenci evleri, dernekler ve kurumlar için.", contact: "İletişim", getStarted: "Başla", login: "Giriş yap", tryFree: "Ücretsiz dene" },
    contact: { tag: "İletişim", title: "Sorular, fikirler veya yardım mı lazım?", sub: "Bir mesaj bırakın — en kısa sürede size geri döneriz.", types: { vraag: "Soru", suggestie: "Öneri", support: "Destek" }, name: "Ad", email: "E-posta adresi", message: "Mesajınız", placeholder: "Size nasıl yardımcı olabiliriz?", send: "Gönder", sending: "Gönderiliyor…", ok: "Teşekkürler! Mesajınızı aldık. E-posta ile onay alacaksınız.", err: "Bir şeyler ters gitti. Lütfen tekrar deneyin." },
    demo: {
      greeting: "Günaydın, Sanne 👋", pinned: "Sabitlendi", wifiTitle: "WiFi şifresi değişti", wifiBody: "Mutfaktaki ilan panosunda.",
      openTasks: "Açık görevler", forYou: "Senin için", task1: "Bulaşık yıka", task2: "Oturma odasını süpür",
      roosterTitle: "Program", roosterSub: "Görevleri başka bir güne sürükle", postponed: "Süpürme ertelendi → Çar",
      etenTitle: "Bugün ne yiyoruz?", etenSub: "Ev arkadaşlarınla oy ver", meal1: "Pizza gecesi", meal2: "Çin yemeği",
      hoppaTitle: "Hoppa pazar yeri", item1: "Kablosuz matkap", lend: "ÖDÜNÇ", item1sub: "Uç seti dahil · Thomas · Utrecht", item2: "Şehir bisikleti — kirala", perWeek: "/hf",
      days: ["Pt", "Sa", "Ça", "Pe", "Cu", "Ct", "Pz"],
      kioskHeader: "Kiosk · Çarşamba", kTask1: "Bulaşık yıka — Sanne", kTask2: "Çöpü çıkar — Daan", kTask3: "Bitkileri sula — Emma",
      important: "Önemli", meeting: "Ev toplantısı Cu 19:30", whatEat: "Ne yiyoruz?", pasta: "Makarna 🍝",
      agenda: "Takvim", shopTitle: "Alışveriş", shop1: "Süt", shop2: "Ekmek",
    },
  },
  ar: {
    nav: { features: "الميزات", kiosk: "الكشك", reviews: "التقييمات", pricing: "الأسعار", login: "تسجيل الدخول", tryFree: "جرّب مجانًا" },
    hero: {
      badge: "للعائلات وبيوت الطلاب والجمعيات والمؤسسات",
      titleA: "أخيرًا وضوح في ", titleB: "فوضى التنظيم", titleC: ".",
      sub: "المهام والوجبات والتسوّق والجميع في مكان واحد — بدون رسائل لا تنتهي. يجعل Slackr منزلك أو عائلتك أو ناديك يعمل بسلاسة.",
      cta: "جرّب مجانًا", how: "كيف يعمل",
      trial: "{d} يومًا مجانًا · بدون بطاقة ائتمان",
      toastDone: "اكتملت المهمة!", birthday: "عيد ميلاد Daan 🎂",
    },
    kiosk: {
      tag: "وضع الكشك", title: "القلب الرقمي لمنزلك، على الحائط.",
      sub: "علّق جهازًا لوحيًا (11 بوصة) كلوحة فنية ويبقى الجميع على اطلاع دائم. الإعلانات في الأعلى، جدول اليوم، تصويت الطعام وتسجيل دخول سريع برمز PIN — بدون تسجيل الدخول.",
      points: ["الإعلانات دائمًا في الأعلى", "استلم مهمة بسرعة برمزك", "تصويت مباشر: ماذا نأكل اليوم؟", "قائمة تسوّق مشتركة — أشِر مباشرة (رابط/QR)", "زر التقويم: أضف الأنشطة إلى تقويمك الخاص"],
    },
    multilang: {
      tag: "متعدّد اللغات", title: "كلٌّ بلغته",
      sub: "يتحدّث Slackr الهولندية والإنجليزية والتركية والعربية. يختار كل فرد لغته — يُترجَم التطبيق والإشعارات ورسائل الآخرين تلقائيًا.",
      points: ["التطبيق والجولة بأربع لغات", "الرسائل تُترجَم تلقائيًا إلى لغتك", "لوحة الكشك باللغة التي يختارها المشرف", "دعم كامل للكتابة من اليمين لليسار (العربية)"],
      example: "مثال: ينشر يوسف إعلانًا بالعربية، وتقرؤه سانه بالهولندية ومرت بالتركية — دون أن يترجم أحد شيئًا.",
    },
    features: {
      header: "كل ما يحتاجه المنزل، في تطبيق واحد.", example: "مثال",
      kbPreview: { tag: "كيف", title: "تنظيف ماكينة القهوة", steps: ["أطفئها واتركها تبرد", "اشطف الفلتر بماء دافئ", "أزل الترسبات مرة شهريًا"] },
      items: [
        { badge: "المهام والجدول", title: "توزيع عادل وتخطيط سلس",
          text: "اسحب المهام بين الأيام في الجدول المرئي، وأجّلها بنقرة واحدة (\"لا أستطيع اليوم، لكن الجمعة مناسب\") ودع المخطّط التلقائي يوزّعها بعدل.",
          scenario: "Daan مريض؟ اسحب مهمته إلى الغد — يراها الجميع فورًا على الكشك." },
        { badge: "الطعام والتسوّق", title: "لا مزيد من \u2018ماذا نأكل اليوم؟\u2019",
          text: "وصفات مع محسّن الحصص، وقوائم تسوّق ذكية، وتصويت يومي بالصور للاختيار معًا. شارك القائمة عبر رابط أو QR، وأشِر إلى العناصر مباشرة على الكيوسك، وأضف وصفة إلى قائمتك بنقرة واحدة.",
          scenario: "تصويت بالصور: \u2018باستا بيستو\u2019 أم \u2018طعام صيني\u2019؟ يصوّت المنزل، ويظهر الفائز على اللوحة." },
        { badge: "سوق هوبا", title: "استعِر أو استأجر أو اشترِ أو مجانًا",
          text: "سوقك الخاص داخل منزلك أو مؤسستك. مثقاب للإعارة، مكتب قديم للبيع أو صناديق نقل مجانية — مع أوصاف بالذكاء الاصطناعي.",
          scenario: "\u2018من لديه مثقاب؟\u2019 انشره على هوبا وانتهى الأمر — متاح لمدة 8 أسابيع." },
        { badge: "الأعمال وشؤون المنزل", title: "من صنبور مكسور إلى حفلة منزلية",
          text: "أبلغ عن الأعمال كتذاكر لفريق الصيانة، ثبّت الإعلانات المهمة في الأعلى، واحتفل بأعياد الميلاد بتذكيرات هدايا تلقائية.",
          scenario: "صنبور يسرّب؟ أنشئ عملًا مع صورة — يتولاه المشرف." },
        { badge: "صناديق الهدايا", title: "ادّخروا معًا للهدية المثالية",
          text: "ابدأ صندوق هدايا لأي مناسبة — عيد ميلاد أو وداع أو حفل منزل أو شيء لطيف للبيت. حدّد هدفًا، واجمع أفكار الهدايا، وأضف رابط Tikkie أو IBAN. تختار مستفيدًا؟ يبقى الصندوق سرًّا عنه.",
          scenario: "يان سينتقل؟ ابدأ صندوقًا، ويساهم الجميع عبر رابط الدفع — ولا يرى يان شيئًا. \ud83e\udd2b" },
        { badge: "معرفة المنزل + قاعدة المعرفة", title: "اشرح كيف يعمل كل شيء في المنزل",
          text: "من تنظيف ماكينة القهوة إلى تشغيل رشّاشات الحديقة — وثّق بالصور والخطوات كيف تعمل الأجهزة والأعمال في المنزل وحوله. يقرؤها أفراد المنزل الجدد، ويجيب Wizzy AI عن الباقي.",
          scenario: "\u2018كيف أشغّل الرشّاشات؟\u2019 موجودة في قاعدة المعرفة بالصور — يشرح Wizzy الخطوات." },
      ],
    },
    modulesHeader: "والمزيد الكثير",
    modules: [
      { label: "الإعلانات", d: "ثبّت الرسائل المهمة أو جدولها أو اجعلها تتكرر." },
      { label: "الاحتفالات", d: "أعياد الميلاد والحفلات مع تذكيرات هدايا مسبقة." },
      { label: "تبادل المهام", d: "بادِل الأعمال بعدل مع أفراد المنزل عبر سوق التبادل." },
      { label: "قاعدة المعرفة + Wizzy AI", d: "\u2018كيف، ما هو\u2019 — اسأل Wizzy كيف يعمل كل شيء." },
      { label: "Pin Dates", d: "أحداث مهمة مع تقويم وسجل." },
      { label: "الدعوة عبر QR", d: "أفراد جدد يمسحون الرمز ويسجّلون." },
      { label: "مشاركة التسوّق", d: "شارك القائمة عبر رابط أو QR — يشطب الجميع مباشرة، وعلى الكيوسك أيضًا." },
      { label: "مزامنة التقويم", d: "اشترك في مهامك وأنشطتك — تظهر في تقويمك الخاص." },
    ],
    how: {
      header: "ابدأ في 3 خطوات",
      steps: [
        { t: "أنشئ منزلك", d: "أضِف المجموعات والغرف والتفضيلات — في دقائق قليلة." },
        { t: "ادعُ الجميع", d: "شارك رمز QR أو رابط الدعوة. يسجّل أفراد المنزل ويثبّتون التطبيق." },
        { t: "يتولى Slackr الباقي", d: "يحافظ Wizzy AI والمخطّط التلقائي على تشغيل المنزل وتنظيمه." },
      ],
    },
    reviews: {
      avg: "متوسط 4٫9 / 5", header: "ماذا يقول المستخدمون", sub: "من بيوت الطلاب إلى الجمعيات والعائلات.",
      items: [
        { q: "منذ Slackr انتهت فعلًا الرسائل التي لا تنتهي حول من يغسل الأطباق. الجميع يرى ببساطة ما يجب فعله.", n: "Sanne", r: "بيت طلابي، أوترخت" },
        { q: "الكشك على الحائط لا يُقدّر بثمن. يتفقّد الأطفال مهامهم بأنفسهم في الصباح — لا مزيد من الإلحاح.", n: "Mark & Elske", r: "عائلة، 3 أطفال" },
        { q: "نستخدمه لجمعيتنا. الأعمال والجداول والإعلانات في مكان واحد. واضح جدًا.", n: "Youssef", r: "نادٍ رياضي" },
        { q: "أخيرًا تطبيق غير معقّد. خلال 10 دقائق كان منزلنا كله عليه.", n: "Priya", r: "سكن مشترك، روتردام" },
        { q: "تصويتات 'ماذا نأكل اليوم؟' بالصور نجحت كثيرًا. لم يكن تخطيط الوجبات ممتعًا هكذا من قبل.", n: "Tom", r: "بيت طلابي، خرونينجن" },
        { q: "كمؤسسة رعاية نتابع مهام المقيمين والأنشطة بدقة. الفريق سعيد به.", n: "Linda", r: "مؤسسة رعاية" },
      ],
    },
    pricing: {
      header: "باقات بسيطة لكل منزل",
      sub: "ابدأ دائمًا بـ {d} يومًا مجانًا. بدون بطاقة ائتمان.",
      popular: "الأكثر شيوعًا", perMonth: "شهريًا", cta: "جرّب مجانًا",
      months: "شهر",
      plans: [
        { name: "3 أشهر", tag: "قصير وفعّال", feats: ["كل الوحدات", "وضع الكشك", "Wizzy AI", "بدون رسوم إعداد"] },
        { name: "6 أشهر", tag: "الأكثر شيوعًا", feats: ["كل الوحدات", "وضع الكشك", "Wizzy AI", "دعم بالأولوية"] },
        { name: "12 شهرًا", tag: "أفضل قيمة", feats: ["كل الوحدات", "وضع الكشك", "Wizzy AI", "حسابات مشرفين إضافية"] },
      ],
    },
    finalCta: {
      header: "هل أنت مستعد لمنزل مرتّب؟",
      sub: "انضم إلى المنازل التي تركت الفوضى وراءها. سجّل، ثبّت التطبيق وابدأ اليوم.",
      cta: "ابدأ تجربتك المجانية",
    },
    footer: { tagline: "مهام بدون رسائل لا تنتهي — للعائلات وبيوت الطلاب والجمعيات والمؤسسات.", contact: "تواصل", getStarted: "ابدأ", login: "تسجيل الدخول", tryFree: "جرّب مجانًا" },
    contact: { tag: "تواصل", title: "أسئلة أو أفكار أو تحتاج مساعدة؟", sub: "اترك رسالة — سنرد عليك في أقرب وقت ممكن.", types: { vraag: "سؤال", suggestie: "اقتراح", support: "دعم" }, name: "الاسم", email: "البريد الإلكتروني", message: "رسالتك", placeholder: "كيف يمكننا مساعدتك؟", send: "إرسال", sending: "جارٍ الإرسال…", ok: "شكرًا! لقد استلمنا رسالتك. ستصلك رسالة تأكيد بالبريد الإلكتروني.", err: "حدث خطأ ما. حاول مرة أخرى." },
    demo: {
      greeting: "صباح الخير، Sanne 👋", pinned: "مثبّت", wifiTitle: "تم تغيير كلمة مرور WiFi", wifiBody: "معلّقة على لوحة الإعلانات في المطبخ.",
      openTasks: "مهام مفتوحة", forYou: "لك", task1: "غسل الأطباق", task2: "كنس غرفة المعيشة",
      roosterTitle: "الجدول", roosterSub: "اسحب المهام إلى يوم آخر", postponed: "تأجّل الكنس → الأربعاء",
      etenTitle: "ماذا نأكل اليوم؟", etenSub: "صوّت مع أفراد منزلك", meal1: "ليلة بيتزا", meal2: "طعام صيني",
      hoppaTitle: "سوق هوبا", item1: "مثقاب لاسلكي", lend: "إعارة", item1sub: "مع مجموعة رؤوس · Thomas · أوترخت", item2: "دراجة مدينة — إيجار", perWeek: "/أسبوع",
      days: ["إث", "ثل", "أر", "خم", "جم", "سب", "أح"],
      kioskHeader: "الكشك · الأربعاء", kTask1: "غسل الأطباق — Sanne", kTask2: "إخراج القمامة — Daan", kTask3: "سقي النباتات — Emma",
      important: "مهم", meeting: "اجتماع المنزل الجمعة 19:30", whatEat: "ماذا نأكل؟", pasta: "باستا 🍝",
      agenda: "التقويم", shopTitle: "التسوّق", shop1: "حليب", shop2: "خبز",
    },
  },
};

const Reveal = ({ children, delay = 0, className = "" }) => (
  <motion.div className={className}
    initial={{ opacity: 0, y: 28 }} whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-60px" }} transition={{ duration: 0.6, delay, ease: [0.22, 1, 0.36, 1] }}>
    {children}
  </motion.div>
);

const GradText = ({ children }) => (
  <span style={{ background: "linear-gradient(135deg,#8B5CFF,#C84BE0,#F472B6)", WebkitBackgroundClip: "text", backgroundClip: "text", color: "transparent" }}>{children}</span>
);

const SCREEN_VandaAG = ({ d }) => (
  <div className="px-4 py-3 space-y-3">
    <p className="font-head font-black text-xl">{d.greeting}</p>
    <div className="brutal-sm rounded-2xl bg-brand-yellow p-3">
      <div className="flex items-center gap-1 text-[10px] font-black uppercase mb-0.5"><Pin className="w-3 h-3" strokeWidth={3} />{d.pinned}</div>
      <p className="font-head font-bold text-sm leading-tight">{d.wifiTitle}</p>
      <p className="text-xs mt-0.5">{d.wifiBody}</p>
    </div>
    <div className="grid grid-cols-2 gap-2">
      <div className="azure-gradient text-white rounded-2xl p-3 brutal-sm"><p className="text-2xl font-head font-black leading-none">7</p><p className="text-[11px] font-bold mt-1">{d.openTasks}</p></div>
      <div className="bg-white rounded-2xl p-3 brutal-sm"><p className="text-2xl font-head font-black leading-none text-brand-violet">3</p><p className="text-[11px] font-bold mt-1">{d.forYou}</p></div>
    </div>
    {[[d.task1, "#2DD4BF"], [d.task2, "#F472B6"]].map(([t, c]) => (
      <div key={t} className="bg-white rounded-2xl p-3 brutal-sm flex items-center gap-2">
        <span className="w-2.5 h-2.5 rounded-full" style={{ background: c }} />
        <span className="font-bold text-sm flex-1">{t}</span>
        <span className="w-6 h-6 rounded-full bg-brand-green brutal-sm flex items-center justify-center"><Check className="w-3.5 h-3.5" strokeWidth={3} /></span>
      </div>
    ))}
  </div>
);

const SCREEN_ROOSTER = ({ d }) => {
  const chips = { 1: ["#2DD4BF"], 2: ["#F472B6", "#3B82F6"], 4: ["#FBBF24"], 5: ["#8B5CFF"] };
  return (
    <div className="px-4 py-3">
      <p className="font-head font-black text-xl mb-1">{d.roosterTitle}</p>
      <p className="text-xs text-brand-ink/50 font-semibold mb-3">{d.roosterSub}</p>
      <div className="grid grid-cols-7 gap-1">
        {d.days.map((day, k) => <div key={k} className="text-center text-[10px] font-black text-brand-ink/40">{day}</div>)}
        {[...Array(7)].map((_, i) => (
          <div key={i} className={`brutal-sm rounded-lg p-1 min-h-[56px] bg-white ${i === 2 ? "border-2 border-brand-violet" : ""}`}>
            <span className="text-[10px] font-bold text-brand-ink/50">{10 + i}</span>
            {(chips[i] || []).map((c, k) => <div key={k} className="h-1.5 rounded-full mt-1" style={{ background: c }} />)}
          </div>
        ))}
      </div>
      <div className="mt-3 brutal-sm rounded-2xl bg-brand-yellow p-2.5 flex items-center gap-2">
        <CalendarClock className="w-4 h-4" strokeWidth={2.5} />
        <span className="text-xs font-bold">{d.postponed}</span>
      </div>
    </div>
  );
};

const SCREEN_ETEN = ({ d }) => (
  <div className="px-4 py-3">
    <p className="font-head font-black text-xl">{d.etenTitle}</p>
    <p className="text-xs text-brand-ink/50 font-semibold mb-3">{d.etenSub}</p>
    {[[d.meal1, PIZZA, 62, "3"], [d.meal2, NOODLES, 38, "2"]].map(([t, img, pct, n]) => (
      <div key={t} className="bg-white rounded-2xl brutal-sm p-2 mb-2">
        <div className="flex items-center gap-2">
          <img src={img} alt="" className="w-11 h-11 rounded-xl object-cover brutal-sm" />
          <div className="flex-1">
            <div className="flex items-center justify-between"><span className="font-bold text-sm">{t}</span><span className="text-xs font-black text-brand-violet">{n} 🗳️</span></div>
            <div className="h-2 rounded-full bg-brand-ink/10 mt-1.5 overflow-hidden"><div className="h-full azure-gradient" style={{ width: `${pct}%` }} /></div>
          </div>
        </div>
      </div>
    ))}
  </div>
);

const SCREEN_HOPPA = ({ d }) => (
  <div className="px-4 py-3">
    <p className="font-head font-black text-xl mb-3">{d.hoppaTitle}</p>
    <div className="bg-white rounded-2xl brutal-sm overflow-hidden">
      <img src="https://images.pexels.com/photos/30413428/pexels-photo-30413428.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=200&w=300" alt="" className="w-full h-28 object-cover" />
      <div className="p-3">
        <div className="flex items-center justify-between">
          <span className="font-head font-bold text-sm">{d.item1}</span>
          <span className="text-[10px] font-black bg-brand-teal/20 text-brand-teal rounded-full px-2 py-0.5">{d.lend}</span>
        </div>
        <p className="text-xs text-brand-ink/50 mt-1 font-medium">{d.item1sub}</p>
      </div>
    </div>
    <div className="bg-white rounded-2xl brutal-sm p-3 mt-2 flex items-center gap-2">
      <ShoppingBag className="w-5 h-5 text-brand-pink" strokeWidth={2.5} />
      <span className="text-sm font-bold flex-1">{d.item2}</span>
      <span className="text-xs font-black text-brand-violet">€ 5{d.perWeek}</span>
    </div>
  </div>
);

const SCREENS = [
  { render: SCREEN_VandaAG, nav: 0 },
  { render: SCREEN_ROOSTER, nav: 1 },
  { render: SCREEN_ETEN, nav: 2 },
  { render: SCREEN_HOPPA, nav: 4 },
];

function PhoneMockup({ d }) {
  const [idx, setIdx] = useState(0);
  useEffect(() => {
    const iv = setInterval(() => setIdx((i) => (i + 1) % SCREENS.length), 3200);
    return () => clearInterval(iv);
  }, []);
  const Screen = SCREENS[idx].render;
  const navActive = SCREENS[idx].nav;
  return (
    <div className="relative w-[270px] sm:w-[300px] rounded-[2.8rem] border-[10px] border-brand-ink bg-brand-ink shadow-[0_40px_80px_-24px_rgba(124,92,255,0.6)]">
      <div className="absolute top-2 left-1/2 -translate-x-1/2 w-24 h-5 bg-brand-ink rounded-full z-10" />
      <div className="rounded-[2rem] overflow-hidden bg-brand-cream h-[560px] relative flex flex-col">
        <div className="px-4 pt-7 pb-2 flex items-center justify-between bg-brand-cream shrink-0" dir="ltr">
          <div className="flex items-center gap-1.5">
            <div className="azure-gradient w-7 h-7 rounded-lg flex items-center justify-center"><Sparkles className="w-4 h-4 text-white" strokeWidth={3} /></div>
            <span className="font-head font-black text-lg">Slackr</span>
          </div>
          <div className="w-7 h-7 rounded-full bg-white brutal-sm flex items-center justify-center text-xs font-black">S</div>
        </div>
        <div className="flex-1 overflow-hidden relative">
          <AnimatePresence mode="wait">
            <motion.div key={idx} initial={{ opacity: 0, x: 24 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -24 }}
              transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }} className="absolute inset-0">
              <Screen d={d} />
            </motion.div>
          </AnimatePresence>
        </div>
        <div className="flex justify-center gap-1.5 pb-1.5 shrink-0">
          {SCREENS.map((_, i) => <span key={i} className={`h-1.5 rounded-full transition-all ${i === idx ? "w-5 bg-brand-violet" : "w-1.5 bg-brand-ink/20"}`} />)}
        </div>
        <div className="bg-white border-t-2 border-brand-ink flex justify-around py-2 shrink-0">
          {[Home, ListChecks, UtensilsCrossed, Users, Monitor].map((I, i) => (
            <span key={i} className={`p-1.5 rounded-lg transition-colors ${i === navActive ? "bg-brand-orange text-white" : "text-brand-ink/40"}`}><I className="w-4 h-4" strokeWidth={2.5} /></span>
          ))}
        </div>
      </div>
    </div>
  );
}

function KioskFrame({ d }) {
  return (
    <div className="relative rounded-md overflow-hidden shadow-[0_50px_90px_-30px_rgba(27,20,64,0.7)]"
      style={{ border: "16px solid", borderColor: "#6b4b2b", background: "#3b2a18" }}>
      <div className="p-3 bg-[#F7F5FF]" style={{ boxShadow: "inset 0 2px 14px rgba(0,0,0,0.25)" }}>
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <div className="azure-gradient w-8 h-8 rounded-xl flex items-center justify-center"><Sparkles className="w-4 h-4 text-white" strokeWidth={3} /></div>
            <span className="font-head font-black text-xl">Slackr</span>
            <span className="animate-slow-flash brutal-sm rounded-full px-2.5 py-1 text-[10px] font-head font-black inline-flex items-center gap-1 ms-1" style={{ background: "#F5C842", color: "#1a1a1a" }}><CalendarClock className="w-3 h-3" strokeWidth={3} />{d.agenda}</span>
          </div>
          <span className="text-xs font-bold text-brand-ink/50">{d.kioskHeader}</span>
        </div>
        <div className="grid grid-cols-3 gap-3">
          <div className="col-span-2 space-y-2">
            {[d.kTask1, d.kTask2, d.kTask3].map((t, i) => (
              <div key={t} className="bg-white rounded-xl brutal-sm p-2.5 flex items-center gap-2">
                <span className="w-2.5 h-2.5 rounded-full" style={{ background: ["#2DD4BF", "#3B82F6", "#FBBF24"][i] }} />
                <span className="font-bold text-sm">{t}</span>
              </div>
            ))}
          </div>
          <div className="space-y-2">
            <div className="brutal-sm rounded-xl bg-brand-yellow p-2.5">
              <div className="flex items-center gap-1 text-[9px] font-black uppercase"><Pin className="w-2.5 h-2.5" strokeWidth={3} />{d.important}</div>
              <p className="font-head font-bold text-xs leading-tight mt-0.5">{d.meeting}</p>
            </div>
            <div className="azure-gradient text-white rounded-xl p-2.5">
              <p className="text-[10px] font-bold flex items-center gap-1"><Vote className="w-3 h-3" strokeWidth={3} />{d.whatEat}</p>
              <p className="font-head font-black text-sm leading-tight mt-0.5">{d.pasta}</p>
            </div>
            <div className="brutal-sm rounded-xl p-2.5" style={{ background: "#FDE7F1" }}>
              <p className="text-[10px] font-black uppercase flex items-center gap-1 text-brand-ink/60"><ShoppingBag className="w-3 h-3" strokeWidth={3} />{d.shopTitle}</p>
              <p className="text-xs font-bold leading-tight mt-0.5 flex items-center gap-1"><Check className="w-3 h-3" strokeWidth={3} />{d.shop1}</p>
              <p className="text-xs font-bold leading-tight flex items-center gap-1"><Check className="w-3 h-3" strokeWidth={3} />{d.shop2}</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

const KB_PHOTO = "https://images.pexels.com/photos/13735908/pexels-photo-13735908.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=400&w=600";

function KBPreview({ kb, color }) {
  return (
    <div className="w-full max-w-sm brutal rounded-3xl bg-white overflow-hidden">
      <div className="relative">
        <img src={KB_PHOTO} alt="" className="w-full h-40 object-cover" />
        <span className="absolute top-3 start-3 brutal-sm rounded-full bg-white px-2.5 py-1 text-[11px] font-black inline-flex items-center gap-1" style={{ color }}>
          <BookOpen className="w-3 h-3" strokeWidth={2.75} />{kb.tag}
        </span>
      </div>
      <div className="p-5">
        <p className="font-head font-black text-lg leading-tight">{kb.title}</p>
        <ul className="mt-3 space-y-2">
          {kb.steps.map((s, i) => (
            <li key={i} className="flex items-center gap-2.5 text-sm font-semibold">
              <span className="w-6 h-6 rounded-full text-white font-head font-black text-xs flex items-center justify-center shrink-0" style={{ background: color }}>{i + 1}</span>
              {s}
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

const FEATURE_ICONS = [ListChecks, UtensilsCrossed, ShoppingBag, Wrench, Gift, BookOpen];
const FEATURE_COLORS = ["#8B5CFF", "#F472B6", "#2DD4BF", "#FBBF24", "#8B5CFF", "#C84BE0"];
const MODULE_ICONS = [Megaphone, PartyPopper, Repeat, BookOpen, CalendarClock, QrCode, ShoppingBag, CalendarClock];
const STEP_ICONS = [Home, QrCode, Sparkles];

export default function Landing() {
  const navigate = useNavigate();
  const [pricing, setPricing] = useState(null);
  const [showBar, setShowBar] = useState(false);
  const [lang, setLang] = useState(() => localStorage.getItem("slackr_lang") || "nl");
  const c = TX[lang] || TX.nl;
  const rtl = RTL_LANGS.includes(lang);
  const pickLang = (code) => { setLang(code); localStorage.setItem("slackr_lang", code); };

  useEffect(() => { fetch(`${API}/settings/public`).then((r) => r.json()).then((d) => setPricing(d.pricing)).catch(() => {}); }, []);
  useEffect(() => {
    const onScroll = () => setShowBar(window.scrollY > 560);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const trialDays = pricing?.trial_days || 30;
  const priceByMonths = { 3: pricing?.price_3m, 6: pricing?.price_6m, 12: pricing?.price_12m };
  const months = [3, 6, 12];
  const plans = c.pricing.plans.map((p, i) => ({ ...p, months: months[i], price: priceByMonths[months[i]], highlight: i === 1 }));

  const [contactForm, setContactForm] = useState({ name: "", email: "", type: "vraag", message: "" });
  const [contactState, setContactState] = useState("idle"); // idle | sending | ok | err
  const submitContact = async (e) => {
    e.preventDefault();
    if (!contactForm.name.trim() || !contactForm.email.trim() || !contactForm.message.trim()) return;
    setContactState("sending");
    try {
      const res = await fetch(`${API}/public/contact`, {
        method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(contactForm),
      });
      if (!res.ok) throw new Error("failed");
      setContactState("ok");
      setContactForm({ name: "", email: "", type: "vraag", message: "" });
    } catch { setContactState("err"); }
  };

  return (
    <div dir={rtl ? "rtl" : "ltr"} className="min-h-screen bg-brand-cream text-brand-ink overflow-x-hidden">
      {/* NAV */}
      <header className="sticky top-0 z-50 bg-brand-cream/85 backdrop-blur-xl border-b border-brand-ink/8">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="azure-gradient w-9 h-9 rounded-xl flex items-center justify-center"><Sparkles className="w-5 h-5 text-white" strokeWidth={3} /></div>
            <span className="font-head font-black text-2xl tracking-tight">Slackr</span>
          </div>
          <nav className="hidden md:flex items-center gap-8 font-bold text-sm">
            <a href="#features" className="hover:text-brand-violet transition-colors" data-testid="nav-features">{c.nav.features}</a>
            <a href="#kiosk" className="hover:text-brand-violet transition-colors" data-testid="nav-kiosk">{c.nav.kiosk}</a>
            <a href="#reviews" className="hover:text-brand-violet transition-colors" data-testid="nav-reviews">{c.nav.reviews}</a>
            <a href="#prijzen" className="hover:text-brand-violet transition-colors" data-testid="nav-prijzen">{c.nav.pricing}</a>
            <a href="#contact" className="hover:text-brand-violet transition-colors" data-testid="nav-contact">{c.footer.contact}</a>
          </nav>
          <div className="flex items-center gap-2">
            <div className="hidden sm:flex items-center gap-1 me-1" data-testid="landing-langs">
              {LANGS.map((l) => (
                <button key={l.code} data-testid={`landing-lang-${l.code}`} onClick={() => pickLang(l.code)}
                  title={l.label} className={`rounded-full px-1.5 py-1 text-sm brutal-press ${lang === l.code ? "bg-brand-violet text-white brutal-sm" : "opacity-70 hover:opacity-100"}`}>{l.flag}</button>
              ))}
            </div>
            <button onClick={() => navigate("/login")} data-testid="nav-login-btn"
              className="ui-btn px-4 py-2 font-head font-bold text-sm bg-white border-2 border-brand-ink brutal-press">{c.nav.login}</button>
            <button onClick={() => navigate("/aanmelden")} data-testid="nav-cta-btn"
              className="ui-btn px-4 py-2 font-head font-bold text-sm azure-gradient text-white brutal-press">{c.nav.tryFree}</button>
          </div>
        </div>
      </header>

      {/* HERO */}
      <section className="relative max-w-7xl mx-auto px-5 sm:px-8 pt-16 pb-24 grid lg:grid-cols-2 gap-12 items-center">
        <div className="absolute -top-20 -left-24 w-96 h-96 rounded-full opacity-30 blur-3xl" style={{ background: "radial-gradient(circle,#8B5CFF,transparent 70%)" }} />
        <div className="relative">
          <Reveal>
            <span className="inline-flex items-center gap-1.5 brutal-sm rounded-full bg-white px-3 py-1.5 text-xs font-bold mb-5">
              <Star className="w-3.5 h-3.5 text-brand-yellow fill-brand-yellow" strokeWidth={2.5} />{c.hero.badge}
            </span>
          </Reveal>
          <Reveal delay={0.05}>
            <h1 className="font-head font-black tracking-tighter text-5xl sm:text-6xl leading-[0.98]">
              {c.hero.titleA}<GradText>{c.hero.titleB}</GradText>{c.hero.titleC}
            </h1>
          </Reveal>
          <Reveal delay={0.12}>
            <p className="text-lg text-brand-ink/70 mt-5 max-w-md font-medium leading-relaxed">{c.hero.sub}</p>
          </Reveal>
          <Reveal delay={0.18}>
            <div className="flex flex-wrap gap-3 mt-8">
              <button onClick={() => navigate("/aanmelden")} data-testid="hero-cta-btn"
                className="ui-btn px-6 py-3.5 font-head font-bold azure-gradient text-white brutal-press inline-flex items-center gap-2 shadow-[0_18px_40px_-18px_rgba(124,92,255,0.7)]">
                {c.hero.cta} <ArrowRight className={`w-4 h-4 ${rtl ? "rotate-180" : ""}`} strokeWidth={3} />
              </button>
              <a href="#kiosk" className="ui-btn px-6 py-3.5 font-head font-bold bg-white border-2 border-brand-ink brutal-press inline-flex items-center gap-2">{c.hero.how}</a>
            </div>
          </Reveal>
          <Reveal delay={0.24}>
            <p className="text-sm text-brand-ink/50 mt-4 font-semibold">{c.hero.trial.replace("{d}", trialDays)}</p>
          </Reveal>
        </div>
        <div className="relative flex justify-center">
          <motion.div initial={{ opacity: 0, scale: 0.92 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}>
            <PhoneMockup d={c.demo} />
          </motion.div>
          <motion.div className="absolute -right-2 top-16 brutal-sm rounded-2xl bg-white px-3 py-2 hidden sm:flex items-center gap-2"
            animate={{ y: [0, -10, 0] }} transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}>
            <span className="w-7 h-7 rounded-full bg-brand-green flex items-center justify-center"><Check className="w-4 h-4" strokeWidth={3} /></span>
            <span className="text-xs font-bold">{c.hero.toastDone}</span>
          </motion.div>
          <motion.div className="absolute -left-3 bottom-20 brutal-sm rounded-2xl bg-white px-3 py-2 hidden sm:flex items-center gap-2"
            animate={{ y: [0, 10, 0] }} transition={{ duration: 4.5, repeat: Infinity, ease: "easeInOut" }}>
            <PartyPopper className="w-5 h-5 text-brand-pink" strokeWidth={2.5} />
            <span className="text-xs font-bold">{c.hero.birthday}</span>
          </motion.div>
        </div>
      </section>

      {/* KIOSK SHOWCASE */}
      <section id="kiosk" className="relative py-24 scroll-mt-20" style={{ background: "linear-gradient(180deg,#F7F5FF, #EEE9FF)" }}>
        <div className="max-w-7xl mx-auto px-5 sm:px-8 grid lg:grid-cols-2 gap-14 items-center">
          <Reveal>
            <span className="inline-flex items-center gap-1.5 text-xs font-black uppercase tracking-wide text-brand-violet"><Monitor className="w-4 h-4" strokeWidth={3} />{c.kiosk.tag}</span>
            <h2 className="font-head font-black text-3xl sm:text-4xl tracking-tight mt-3">{c.kiosk.title}</h2>
            <p className="text-lg text-brand-ink/70 mt-4 font-medium leading-relaxed">{c.kiosk.sub}</p>
            <ul className="mt-5 space-y-2">
              {c.kiosk.points.map((t) => (
                <li key={t} className="flex items-center gap-2 font-semibold"><span className="w-5 h-5 rounded-full azure-gradient flex items-center justify-center shrink-0"><Check className="w-3 h-3 text-white" strokeWidth={3} /></span>{t}</li>
              ))}
            </ul>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="relative rounded-3xl overflow-hidden p-6 sm:p-10" style={{ backgroundImage: `url(${WALL})`, backgroundSize: "cover", backgroundPosition: "center" }}>
              <div className="absolute inset-0 bg-black/5" />
              <motion.div className="relative" initial={{ y: 12, opacity: 0 }} whileInView={{ y: 0, opacity: 1 }} viewport={{ once: true }} transition={{ duration: 0.6 }}>
                <KioskFrame d={c.demo} />
              </motion.div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* MULTILINGUAL USP */}
      <section className="max-w-7xl mx-auto px-5 sm:px-8 py-24">
        <div className="grid lg:grid-cols-2 gap-14 items-center">
          <Reveal>
            <div className="relative brutal rounded-3xl bg-white p-8 overflow-hidden">
              <div className="absolute -top-16 -right-16 w-52 h-52 rounded-full opacity-20 blur-3xl" style={{ background: "radial-gradient(circle,#C84BE0,transparent 70%)" }} />
              <div className="relative flex flex-wrap gap-2 mb-6">
                {LANGS.map((l) => (
                  <span key={l.code} className="brutal-sm rounded-full bg-brand-cream px-3 py-1.5 text-sm font-bold flex items-center gap-1.5">{l.flag} {l.label}</span>
                ))}
              </div>
              <div className="relative space-y-2 mb-5">
                {[["🇲🇦", "يوسف", "#F472B6"], ["🇳🇱", "Sanne", "#8B5CFF"], ["🇹🇷", "Mert", "#2DD4BF"]].map(([flag, name, col]) => (
                  <div key={name} className="brutal-sm rounded-2xl bg-white p-2.5 flex items-center gap-2">
                    <span className="w-8 h-8 rounded-full flex items-center justify-center text-white font-head font-black shrink-0" style={{ background: col }}>{name[0]}</span>
                    <span className="font-bold text-sm flex-1">{name}</span>
                    <span className="text-lg">{flag}</span>
                  </div>
                ))}
              </div>
              <div className="relative brutal-sm rounded-2xl bg-brand-cream p-4 border-s-4 border-brand-violet">
                <p className="text-xs font-black uppercase tracking-wide text-brand-ink/40 mb-1">{c.features.example}</p>
                <p className="text-sm font-semibold">{c.multilang.example}</p>
              </div>
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <span className="inline-flex items-center gap-1.5 text-xs font-black uppercase tracking-wide text-brand-violet"><Languages className="w-4 h-4" strokeWidth={3} />{c.multilang.tag}</span>
            <h2 className="font-head font-black text-3xl sm:text-4xl tracking-tight mt-3">{c.multilang.title}</h2>
            <p className="text-lg text-brand-ink/70 mt-4 font-medium leading-relaxed">{c.multilang.sub}</p>
            <ul className="mt-5 space-y-2">
              {c.multilang.points.map((t) => (
                <li key={t} className="flex items-center gap-2 font-semibold"><span className="w-5 h-5 rounded-full azure-gradient flex items-center justify-center shrink-0"><Check className="w-3 h-3 text-white" strokeWidth={3} /></span>{t}</li>
              ))}
            </ul>
          </Reveal>
        </div>
      </section>

      {/* FEATURES ALTERNATING */}
      <section id="features" className="max-w-7xl mx-auto px-5 sm:px-8 py-24 scroll-mt-20">
        <Reveal><h2 className="font-head font-black text-3xl sm:text-4xl tracking-tight text-center max-w-2xl mx-auto">{c.features.header}</h2></Reveal>
        <div className="mt-16 space-y-8">
          {c.features.items.map((f, i) => {
            const Icon = FEATURE_ICONS[i]; const color = FEATURE_COLORS[i];
            return (
              <Reveal key={f.title} delay={0.05}>
                <div className="grid md:grid-cols-2 gap-8 items-center">
                  <div className={i % 2 ? "md:order-2" : ""}>
                    <span className="inline-flex items-center gap-1.5 brutal-sm rounded-full bg-white px-3 py-1 text-xs font-black" style={{ color }}>
                      <Icon className="w-3.5 h-3.5" strokeWidth={2.75} />{f.badge}
                    </span>
                    <h3 className="font-head font-black text-2xl sm:text-3xl tracking-tight mt-3">{f.title}</h3>
                    <p className="text-brand-ink/70 mt-3 font-medium leading-relaxed">{f.text}</p>
                    <div className="mt-4 brutal-sm rounded-2xl bg-white p-4 border-s-4" style={{ borderInlineStartColor: color }}>
                      <p className="text-xs font-black uppercase tracking-wide text-brand-ink/40 mb-1">{c.features.example}</p>
                      <p className="text-sm font-semibold">{f.scenario}</p>
                    </div>
                  </div>
                  <div className={`flex justify-center ${i % 2 ? "md:order-1" : ""}`}>
                    {i === 5 ? (
                      <KBPreview kb={c.features.kbPreview} color={color} />
                    ) : (
                      <div className="w-full max-w-sm brutal rounded-3xl p-6 relative overflow-hidden" style={{ background: `linear-gradient(135deg, ${color}18, #ffffff)` }}>
                        <Icon className="w-14 h-14 mb-4" strokeWidth={1.75} style={{ color }} />
                        <div className="space-y-2">
                          {[90, 70, 55].map((w, k) => <div key={k} className="h-3 rounded-full" style={{ width: `${w}%`, background: k === 0 ? color : "rgba(27,20,64,0.08)" }} />)}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </Reveal>
            );
          })}
        </div>

        {/* module bento */}
        <Reveal><h3 className="font-head font-black text-2xl sm:text-3xl tracking-tight text-center mt-24">{c.modulesHeader}</h3></Reveal>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-10">
          {c.modules.map((m, i) => {
            const Icon = MODULE_ICONS[i] || Sparkles;
            return (
              <Reveal key={m.label} delay={i * 0.04}>
                <div className="brutal rounded-3xl bg-white p-6 h-full brutal-press transition-transform hover:-translate-y-1">
                  <div className="w-12 h-12 rounded-2xl azure-gradient flex items-center justify-center mb-4"><Icon className="w-6 h-6 text-white" strokeWidth={2.5} /></div>
                  <p className="font-head font-black text-lg">{m.label}</p>
                  <p className="text-sm text-brand-ink/60 mt-1 font-medium">{m.d}</p>
                </div>
              </Reveal>
            );
          })}
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="py-24" style={{ background: "linear-gradient(180deg,#EEE9FF,#F7F5FF)" }}>
        <div className="max-w-5xl mx-auto px-5 sm:px-8">
          <Reveal><h2 className="font-head font-black text-3xl sm:text-4xl tracking-tight text-center">{c.how.header}</h2></Reveal>
          <div className="grid md:grid-cols-3 gap-6 mt-14">
            {c.how.steps.map((s, i) => {
              const Icon = STEP_ICONS[i];
              return (
                <Reveal key={s.t} delay={i * 0.08}>
                  <div className="brutal rounded-3xl bg-white p-7 h-full relative">
                    <span className="absolute -top-4 -start-2 azure-gradient text-white font-head font-black text-xl w-11 h-11 rounded-2xl flex items-center justify-center brutal-sm">{i + 1}</span>
                    <Icon className="w-9 h-9 text-brand-violet mt-3" strokeWidth={2} />
                    <p className="font-head font-black text-xl mt-3">{s.t}</p>
                    <p className="text-sm text-brand-ink/60 mt-1.5 font-medium leading-relaxed">{s.d}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section id="reviews" className="bg-brand-cream/60 py-24 scroll-mt-20">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <Reveal>
            <div className="text-center">
              <div className="inline-flex items-center gap-2 bg-white brutal-sm rounded-full px-4 py-1.5 mb-4">
                <div className="flex">{[...Array(5)].map((_, i) => <Star key={i} className="w-4 h-4 text-brand-yellow fill-brand-yellow" strokeWidth={2} />)}</div>
                <span className="font-head font-bold text-sm">{c.reviews.avg}</span>
              </div>
              <h2 className="font-head font-black text-3xl sm:text-4xl tracking-tight">{c.reviews.header}</h2>
              <p className="text-brand-ink/60 mt-3 font-medium">{c.reviews.sub}</p>
            </div>
          </Reveal>
          <div className="grid md:grid-cols-3 gap-6 mt-14">
            {c.reviews.items.map((t, i) => (
              <Reveal key={t.n + i} delay={(i % 3) * 0.07}>
                <div className="brutal rounded-3xl bg-white p-7 h-full flex flex-col">
                  <Quote className="w-8 h-8 text-brand-violet/30" strokeWidth={2.5} />
                  <p className="font-medium text-brand-ink/80 mt-3 leading-relaxed flex-1">"{t.q}"</p>
                  <div className="flex mt-4 mb-2">{[...Array(5)].map((_, s) => <Star key={s} className="w-4 h-4 text-brand-yellow fill-brand-yellow" strokeWidth={2} />)}</div>
                  <div className="flex items-center gap-3 pt-3 border-t border-brand-ink/10">
                    <div className="w-10 h-10 rounded-full azure-gradient text-white flex items-center justify-center font-head font-black">{t.n[0]}</div>
                    <div><p className="font-head font-bold leading-tight">{t.n}</p><p className="text-xs text-brand-ink/50 font-semibold">{t.r}</p></div>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* PRICING */}
      <section id="prijzen" className="max-w-7xl mx-auto px-5 sm:px-8 py-24 scroll-mt-20">
        <Reveal>
          <h2 className="font-head font-black text-3xl sm:text-4xl tracking-tight text-center">{c.pricing.header}</h2>
          <p className="text-center text-brand-ink/60 mt-3 font-medium">{c.pricing.sub.replace("{d}", trialDays)}</p>
        </Reveal>
        <div className="grid md:grid-cols-3 gap-6 mt-14 items-stretch">
          {plans.map((p, i) => (
            <Reveal key={p.name} delay={i * 0.07}>
              <div className={`rounded-3xl p-7 h-full flex flex-col relative ${p.highlight ? "azure-gradient text-white brutal shadow-[0_30px_70px_-24px_rgba(124,92,255,0.8)] md:-mt-4" : "bg-white brutal"}`}>
                {p.highlight && <span className="absolute -top-3 left-1/2 -translate-x-1/2 bg-brand-yellow text-brand-ink font-head font-black text-xs px-3 py-1 rounded-full brutal-sm">{c.pricing.popular}</span>}
                <p className={`font-black text-xs uppercase tracking-wide ${p.highlight ? "text-white/80" : "text-brand-violet"}`}>{p.tag}</p>
                <p className="font-head font-black text-2xl mt-1">{p.name}</p>
                <div className="mt-4">
                  <span className="font-head font-black text-4xl">{euro(p.price) || "—"}</span>
                  {p.price && <span className={`text-sm font-semibold ${p.highlight ? "text-white/70" : "text-brand-ink/50"}`}> / {p.months} {c.pricing.months}</span>}
                </div>
                {p.price && <p className={`text-xs mt-1 font-semibold ${p.highlight ? "text-white/70" : "text-brand-ink/50"}`}>± {euro(Math.round(p.price / p.months))} {c.pricing.perMonth}</p>}
                <ul className="mt-6 space-y-2.5 flex-1">
                  {p.feats.map((f) => (
                    <li key={f} className="flex items-center gap-2 text-sm font-semibold">
                      <span className={`w-5 h-5 rounded-full flex items-center justify-center shrink-0 ${p.highlight ? "bg-white/25" : "bg-brand-green"}`}><Check className="w-3 h-3" strokeWidth={3} /></span>{f}
                    </li>
                  ))}
                </ul>
                <button onClick={() => navigate("/aanmelden")} data-testid={`plan-cta-${p.months}`}
                  className={`ui-btn mt-6 w-full py-3 font-head font-bold brutal-press ${p.highlight ? "bg-white text-brand-ink" : "azure-gradient text-white"}`}>
                  {c.pricing.cta}
                </button>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* SOCIAL / FINAL CTA */}
      <section className="max-w-7xl mx-auto px-5 sm:px-8 pb-24">
        <Reveal>
          <div className="relative rounded-[2rem] overflow-hidden navy-gradient text-white p-10 sm:p-16 text-center">
            <img src={STUDENTS} alt="" className="absolute inset-0 w-full h-full object-cover opacity-15" />
            <div className="relative">
              <Users className="w-10 h-10 mx-auto mb-4 opacity-80" strokeWidth={2} />
              <h2 className="font-head font-black text-3xl sm:text-5xl tracking-tight">{c.finalCta.header}</h2>
              <p className="text-white/70 mt-4 max-w-lg mx-auto font-medium">{c.finalCta.sub}</p>
              <button onClick={() => navigate("/aanmelden")} data-testid="final-cta-btn"
                className="ui-btn mt-8 px-8 py-4 font-head font-bold bg-white text-brand-ink brutal-press inline-flex items-center gap-2">
                {c.finalCta.cta} <ArrowRight className={`w-5 h-5 ${rtl ? "rotate-180" : ""}`} strokeWidth={3} />
              </button>
            </div>
          </div>
        </Reveal>
      </section>

      {/* CONTACT */}
      <section id="contact" className="max-w-3xl mx-auto px-5 sm:px-8 pb-24 scroll-mt-20">
        <Reveal>
          <div className="brutal bg-white rounded-[2rem] p-7 sm:p-10" data-testid="contact-section">
            <span className="inline-block text-xs font-head font-black uppercase tracking-wider bg-brand-violet text-white rounded-full px-3 py-1">{c.contact.tag}</span>
            <h2 className="font-head font-black text-3xl sm:text-4xl tracking-tight mt-3">{c.contact.title}</h2>
            <p className="text-brand-ink/60 mt-2 font-medium">{c.contact.sub}</p>

            {contactState === "ok" ? (
              <div className="mt-6 brutal-sm rounded-2xl bg-brand-green/20 p-6 flex items-start gap-3" data-testid="contact-success">
                <div className="bg-brand-green rounded-full p-1.5 shrink-0"><Check className="w-5 h-5 text-white" strokeWidth={3} /></div>
                <div>
                  <p className="font-head font-bold">{c.contact.ok}</p>
                  <button onClick={() => setContactState("idle")} className="text-sm font-bold text-brand-violet underline mt-2" data-testid="contact-again-btn">{c.contact.tag}</button>
                </div>
              </div>
            ) : (
              <form onSubmit={submitContact} className="mt-6 space-y-4" data-testid="contact-form">
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-bold">{c.contact.name}</label>
                    <input data-testid="contact-name" value={contactForm.name} required
                      onChange={(e) => setContactForm({ ...contactForm, name: e.target.value })}
                      className="w-full brutal-sm rounded-xl px-4 py-3 font-medium outline-none mt-1" />
                  </div>
                  <div>
                    <label className="text-sm font-bold">{c.contact.email}</label>
                    <input data-testid="contact-email" type="email" value={contactForm.email} required
                      onChange={(e) => setContactForm({ ...contactForm, email: e.target.value })}
                      className="w-full brutal-sm rounded-xl px-4 py-3 font-medium outline-none mt-1" />
                  </div>
                </div>
                <div>
                  <label className="text-sm font-bold">{c.contact.tag}</label>
                  <div className="grid grid-cols-3 gap-2 mt-1" data-testid="contact-types">
                    {["vraag", "suggestie", "support"].map((tp) => (
                      <button key={tp} type="button" data-testid={`contact-type-${tp}`}
                        onClick={() => setContactForm({ ...contactForm, type: tp })}
                        className={`brutal-sm brutal-press rounded-xl px-3 py-2.5 text-sm font-bold ${contactForm.type === tp ? "bg-brand-violet text-white" : "bg-brand-cream"}`}>
                        {c.contact.types[tp]}
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <label className="text-sm font-bold">{c.contact.message}</label>
                  <textarea data-testid="contact-message" value={contactForm.message} required rows={4}
                    placeholder={c.contact.placeholder}
                    onChange={(e) => setContactForm({ ...contactForm, message: e.target.value })}
                    className="w-full brutal-sm rounded-xl px-4 py-3 font-medium outline-none mt-1 resize-none" />
                </div>
                {contactState === "err" && <p className="text-sm font-bold text-brand-pink" data-testid="contact-error">{c.contact.err}</p>}
                <button type="submit" disabled={contactState === "sending"} data-testid="contact-submit"
                  className="ui-btn w-full py-3.5 font-head font-bold azure-gradient text-white brutal-press disabled:opacity-60 flex items-center justify-center gap-2">
                  <Mail className="w-5 h-5" strokeWidth={2.5} />{contactState === "sending" ? c.contact.sending : c.contact.send}
                </button>
              </form>
            )}
          </div>
        </Reveal>
      </section>

      {/* FOOTER */}
      <footer className="border-t border-brand-ink/10 bg-white">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-12 grid gap-8 sm:grid-cols-3">
          <div>
            <div className="flex items-center gap-2 mb-3">
              <div className="azure-gradient w-8 h-8 rounded-lg flex items-center justify-center"><Sparkles className="w-4 h-4 text-white" strokeWidth={3} /></div>
              <span className="font-head font-black text-xl">Slackr</span>
            </div>
            <p className="text-sm text-brand-ink/60 font-medium max-w-xs">{c.footer.tagline}</p>
          </div>
          <div>
            <p className="font-head font-bold mb-3">{c.footer.contact}</p>
            <ul className="space-y-2 text-sm text-brand-ink/70 font-medium" data-testid="footer-contact">
              <li className="font-bold text-brand-ink">CHEPL bv</li>
              <li><a href="mailto:info@jobgrid.nl" className="inline-flex items-center gap-2 hover:text-brand-violet transition-colors"><Mail className="w-4 h-4" strokeWidth={2.5} />info@jobgrid.nl</a></li>
              <li><a href="tel:+31302072501" className="inline-flex items-center gap-2 hover:text-brand-violet transition-colors"><Phone className="w-4 h-4" strokeWidth={2.5} />030 207 2501</a></li>
              <li className="text-xs text-brand-ink/50 pt-1">KvK 42087917 · BTW NL869653817B01</li>
            </ul>
          </div>
          <div className="sm:text-end">
            <p className="font-head font-bold mb-3">{c.footer.getStarted}</p>
            <div className="flex sm:justify-end items-center gap-4 text-sm font-bold">
              <Link to="/login" className="hover:text-brand-violet transition-colors">{c.footer.login}</Link>
              <Link to="/aanmelden" className="hover:text-brand-violet transition-colors">{c.footer.tryFree}</Link>
            </div>
          </div>
        </div>
        <div className="border-t border-brand-ink/10">
          <p className="max-w-7xl mx-auto px-5 sm:px-8 py-5 text-sm text-brand-ink/50 font-semibold text-center">© {new Date().getFullYear()} CHEPL bv · Slackr</p>
        </div>
      </footer>

      <AnimatePresence>
        {showBar && (
          <motion.div key="mbar" initial={{ y: 90 }} animate={{ y: 0 }} exit={{ y: 90 }} transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className="md:hidden fixed bottom-0 inset-x-0 z-40 bg-white/95 backdrop-blur-xl border-t-2 border-brand-ink px-4 py-3">
            <button onClick={() => navigate("/aanmelden")} data-testid="sticky-cta-btn"
              className="ui-btn w-full py-3.5 font-head font-bold azure-gradient text-white brutal-press flex items-center justify-center gap-2">
              {c.nav.tryFree} <ArrowRight className={`w-4 h-4 ${rtl ? "rotate-180" : ""}`} strokeWidth={3} />
            </button>
          </motion.div>
        )}
      </AnimatePresence>
      <AnimatePresence>
        {showBar && (
          <motion.button key="totop" initial={{ opacity: 0, scale: 0.8 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.8 }}
            onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })} data-testid="back-to-top-btn"
            className="hidden md:flex fixed bottom-6 end-6 z-40 w-12 h-12 rounded-full azure-gradient text-white brutal items-center justify-center">
            <ArrowUp className="w-5 h-5" strokeWidth={3} />
          </motion.button>
        )}
      </AnimatePresence>
    </div>
  );
}
